import React from "react";
import { Link } from "react-router-dom";
import { Reveal, RevealItem } from "../components/Reveal";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { Breadcrumb } from "../components/Breadcrumb";
import { BREADCRUMBS } from "../lib/pageMetadata";
import {
    Wrench,
    Heart,
    Users,
    Compass,
    ArrowRight,
} from "lucide-react";

/**
 * /about — the honest, opinionated story of why this exists.
 * Four sections per the brief:
 *  1. Why we built Cybersecurity Forefront
 *  2. What we believe
 *  3. Who's behind it
 *  4. What's coming
 */
export const About = () => {
    return (
        <>
            <SEO
                title="About — Why we built Cybersecurity Forefront"
                description="A senior-engineer-led, MSP-first security platform. We built Cybersecurity Forefront because firewall provisioning, client IPAM, and compliance tooling are stuck in 2008. Here's our take."
                canonicalPath="/about"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "AboutPage",
                    name: "About Cybersecurity Forefront",
                    url: `${SITE_DOMAIN}/about`,
                    isPartOf: {
                        "@type": "WebSite",
                        url: `${SITE_DOMAIN}/`,
                        name: "Cybersecurity Forefront",
                    },
                }}
            />

            <div
                className="pt-24 md:pt-28 pb-6 mx-auto max-w-4xl px-5 md:px-10"
                data-testid="about-page"
            >
                <Breadcrumb items={BREADCRUMBS.about} />
                {/* Hero */}
                <Reveal>
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        About
                    </p>
                    <h1
                        className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-3xl"
                        data-testid="about-title"
                    >
                        Built by engineers who got tired of{" "}
                        <span className="text-accent-cyan">2008-era tooling</span>.
                    </h1>
                    <p
                        className="mt-6 text-text-muted leading-relaxed max-w-2xl text-[15px]"
                        data-testid="about-intro"
                    >
                        Cybersecurity Forefront is a senior-engineer-led security platform
                        for MSPs and in-house teams. We ship publicly, charge fairly, and
                        say no to the toil that most of the industry quietly bills hourly.
                    </p>
                </Reveal>

                {/* Section 1 — Why we built it */}
                <section className="mt-24" data-testid="about-section-why">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Wrench className="w-4 h-4" />
                            </div>
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">
                                01 — The why
                            </p>
                        </div>
                        <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight max-w-3xl">
                            Why we built Cybersecurity Forefront
                        </h2>
                        <div className="mt-6 space-y-5 text-[14.5px] text-text-muted leading-[1.85] max-w-2xl">
                            <p>
                                Firewall provisioning shouldn&apos;t require a Friday night.
                                Client IPAM shouldn&apos;t live in an Excel file.
                                Compliance assessments shouldn&apos;t cost €15,000 for a
                                checklist a junior consultant ran.
                            </p>
                            <p>
                                After more than a decade of running networks and security
                                operations for clients across the EU and US, we kept hitting
                                the same wall: the tools that do the work properly are
                                enterprise-priced, the tools that are accessible are toys,
                                and the middle is a graveyard of legacy MSP suites that
                                still ship Internet Explorer 11 fixes.
                            </p>
                            <p>
                                So we started building what we actually wanted to use: a
                                chat-first firewall configurator that speaks Fortinet,
                                WatchGuard, Zyxel, and pfSense; a Client IPAM that holds
                                subnets, hardware, and credentials in one auditable place;
                                and free compliance tools that don&apos;t hide behind a
                                &ldquo;Contact sales&rdquo; button.
                            </p>
                        </div>
                    </Reveal>
                </section>

                {/* Section 2 — What we believe */}
                <section className="mt-24" data-testid="about-section-believe">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Heart className="w-4 h-4" />
                            </div>
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">
                                02 — Beliefs
                            </p>
                        </div>
                        <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight max-w-3xl">
                            What we believe
                        </h2>
                    </Reveal>
                    <Reveal stagger className="mt-8 grid gap-4 md:grid-cols-2">
                        {[
                            {
                                title: "Configs should be reviewable, not magical.",
                                body: "Every line our wizard generates is human-readable, diff-able, and copy-pasteable. No black boxes. No proprietary blob you can&apos;t audit.",
                            },
                            {
                                title: "Pricing should be predictable.",
                                body: "Per-seat or per-tenant. No surprise overages. No mandatory annual contracts. If you have to call sales to learn the price, that&apos;s a tell.",
                            },
                            {
                                title: "Free should mean free.",
                                body: "Our subnet calculator, DNS health check, TLS inspector, security headers scanner, breach lookup, and CVE search are free forever — no email gate, no quota gimmicks.",
                            },
                            {
                                title: "Roadmaps should be public.",
                                body: "We tell you what&apos;s coming, what&apos;s slipped, and what we cut. No NDAs to read a release note. No marketing slide that lies about &lsquo;AI&rsquo;.",
                            },
                            {
                                title: "Compliance is a deliverable, not a religion.",
                                body: "NIS2, SOC 2, CMMC — they&apos;re paperwork until you can map them to controls you actually run. We focus on the second part.",
                            },
                            {
                                title: "Senior engineers do the work.",
                                body: "Every customer-facing engagement is led by someone who has actually configured a Fortinet HA pair at 2 AM. Junior support is fine — junior architecture is malpractice.",
                            },
                        ].map((v) => (
                            <RevealItem
                                key={v.title}
                                className="rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-5"
                            >
                                <h3
                                    className="text-[15px] font-semibold text-text-primary"
                                    dangerouslySetInnerHTML={{ __html: v.title }}
                                />
                                <p
                                    className="mt-2 text-[13.5px] text-text-muted leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: v.body }}
                                />
                            </RevealItem>
                        ))}
                    </Reveal>
                </section>

                {/* Section 3 — Who's behind it */}
                <section className="mt-24" data-testid="about-section-team">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Users className="w-4 h-4" />
                            </div>
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">
                                03 — The team
                            </p>
                        </div>
                        <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight max-w-3xl">
                            Who&apos;s behind it
                        </h2>
                        <div className="mt-6 space-y-5 text-[14.5px] text-text-muted leading-[1.85] max-w-2xl">
                            <p>
                                Cybersecurity Forefront is a Wyoming LLC operated by a small
                                core team with 10+ years of hands-on cybersecurity and
                                network engineering experience across SMB, MSP, and
                                enterprise environments in Italy and the United States.
                            </p>
                            <p>
                                We&apos;ve done greenfield branch rollouts, post-breach
                                forensic work, EDR deployments measured in thousands of
                                endpoints, and the unglamorous IPAM cleanup nobody wants to
                                inherit. The product reflects that scar tissue.
                            </p>
                            <p>
                                We&apos;re hiring quietly. If you&apos;re a senior network
                                or security engineer who reads RFCs for fun and would rather
                                ship software than sit in a SOC chair forever, write to{" "}
                                <a
                                    href="mailto:info@cybersecurityforefront.com"
                                    className="text-accent-cyan hover:underline"
                                >
                                    info@cybersecurityforefront.com
                                </a>
                                .
                            </p>
                        </div>
                    </Reveal>
                </section>

                {/* Section 4 — What's coming */}
                <section className="mt-24 mb-12" data-testid="about-section-coming">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Compass className="w-4 h-4" />
                            </div>
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">
                                04 — Roadmap
                            </p>
                        </div>
                        <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight max-w-3xl">
                            What&apos;s coming
                        </h2>
                        <p className="mt-6 text-[14.5px] text-text-muted leading-relaxed max-w-2xl">
                            We ship publicly. Here&apos;s the next 12 months, in priority
                            order. Dates slip. We update this page when they do.
                        </p>
                    </Reveal>
                    <Reveal stagger className="mt-8 grid gap-3">
                        {[
                            {
                                tag: "Q2 2026",
                                title: "Weekly CVE scan",
                                body: "Automated weekly scans against your hardware inventory, with CPE-matched alerts and a one-click NIS2-aligned advisory.",
                            },
                            {
                                tag: "Q2 2026",
                                title: "Multi-framework compliance",
                                body: "NIST CSF 2.0, CMMC, SOC 2, HIPAA — same questionnaire engine, mapped to controls you actually run.",
                            },
                            {
                                tag: "Q3 2026",
                                title: "NIS2 Isolation Planner",
                                body: "Visual segmentation planner that turns your IPAM into NIS2-aligned zones and produces vendor-correct firewall rules.",
                            },
                            {
                                tag: "Q3 2026",
                                title: "Community Discord",
                                body: "A public space for MSPs and security engineers to share configs, post-mortems, and feature requests.",
                            },
                            {
                                tag: "Q4 2026",
                                title: "PCAP analyzer",
                                body: "Upload a capture, get a senior-analyst summary: top talkers, anomalies, suspected exfil, suggested rules.",
                            },
                        ].map((r) => (
                            <RevealItem
                                key={r.title}
                                className="rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-5 flex flex-col md:flex-row md:items-start md:gap-6"
                            >
                                <span className="inline-flex shrink-0 px-2 py-0.5 rounded-md bg-accent-cyan/10 border border-accent-cyan/30 text-accent-cyan text-[10.5px] font-mono uppercase tracking-[0.18em] mb-2 md:mb-0 self-start">
                                    {r.tag}
                                </span>
                                <div>
                                    <h3 className="text-[15px] font-semibold text-text-primary">
                                        {r.title}
                                    </h3>
                                    <p className="mt-1.5 text-[13.5px] text-text-muted leading-relaxed">
                                        {r.body}
                                    </p>
                                </div>
                            </RevealItem>
                        ))}
                    </Reveal>
                    <Reveal>
                        <div className="mt-10 flex flex-col md:flex-row gap-3">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent-cyan text-canvas font-medium text-[13.5px] hover:bg-accent-cyan/90 transition-colors"
                                data-testid="about-cta-contact"
                            >
                                Talk to us
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                            <Link
                                to="/tools"
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-white/10 text-text-primary font-medium text-[13.5px] hover:border-white/20 transition-colors"
                                data-testid="about-cta-tools"
                            >
                                Browse free tools
                            </Link>
                        </div>
                    </Reveal>
                </section>
            </div>
        </>
    );
};

export default About;
